import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  Filter,
  Download,
  RefreshCw,
  Building2,
  IndianRupee,
  Search,
  ChevronLeft,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const AdminDashboard = ({ onBack }) => {
  const [franchiseLeads, setFranchiseLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("franchise");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [franchiseRes, contactsRes] = await Promise.all([
        axios.get(`${API}/franchise-enquiries`),
        axios.get(`${API}/contacts`),
      ]);
      setFranchiseLeads(franchiseRes.data || []);
      setContacts(contactsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus, type) => {
    try {
      // In a real app, this would call an API to update status
      if (type === "franchise") {
        setFranchiseLeads((prev) =>
          prev.map((lead) =>
            lead.id === id ? { ...lead, status: newStatus } : lead
          )
        );
      } else {
        setContacts((prev) =>
          prev.map((contact) =>
            contact.id === id ? { ...contact, status: newStatus } : contact
          )
        );
      }
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredFranchiseLeads = franchiseLeads.filter((lead) => {
    const matchesSearch =
      lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery);
    const matchesStatus =
      statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.subject?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || contact.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    totalFranchise: franchiseLeads.length,
    pendingFranchise: franchiseLeads.filter((l) => l.status === "pending").length,
    totalContacts: contacts.length,
    pendingContacts: contacts.filter((c) => c.status === "pending").length,
  };

  const exportToCSV = () => {
    const data = activeTab === "franchise" ? filteredFranchiseLeads : filteredContacts;
    const headers = activeTab === "franchise" 
      ? ["Name", "Phone", "City", "Investment", "Status", "Date"]
      : ["Name", "Email", "Phone", "Subject", "Status", "Date"];
    
    const csvContent = [
      headers.join(","),
      ...data.map((item) =>
        activeTab === "franchise"
          ? [item.name, item.phone, item.city, item.investment, item.status, item.created_at].join(",")
          : [item.name, item.email, item.phone, item.subject, item.status, item.created_at].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_leads_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("Data exported successfully");
  };

  return (
    <div className="min-h-screen bg-background p-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-foreground-muted hover:text-foreground"
              data-testid="admin-back-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Site
            </Button>
            <div>
              <h1 className="font-heading text-3xl text-foreground">Admin Dashboard</h1>
              <p className="text-foreground-muted">Manage franchise leads and contacts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-white/20"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={exportToCSV}
              className="bg-primary text-black"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-foreground-muted text-sm">Franchise Leads</p>
                <p className="font-heading text-2xl text-foreground">{stats.totalFranchise}</p>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-foreground-muted text-sm">Pending</p>
                <p className="font-heading text-2xl text-accent">{stats.pendingFranchise}</p>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-foreground-muted text-sm">Contact Messages</p>
                <p className="font-heading text-2xl text-foreground">{stats.totalContacts}</p>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-foreground-muted text-sm">Conversion Rate</p>
                <p className="font-heading text-2xl text-green-500">
                  {stats.totalFranchise > 0
                    ? Math.round(
                        ((stats.totalFranchise - stats.pendingFranchise) /
                          stats.totalFranchise) *
                          100
                      )
                    : 0}
                  %
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab("franchise")}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === "franchise"
                ? "bg-primary text-black"
                : "bg-white/5 text-foreground-muted hover:bg-white/10"
            }`}
            data-testid="tab-franchise"
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Franchise Leads ({stats.totalFranchise})
          </button>
          <button
            onClick={() => setActiveTab("contacts")}
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === "contacts"
                ? "bg-primary text-black"
                : "bg-white/5 text-foreground-muted hover:bg-white/10"
            }`}
            data-testid="tab-contacts"
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Contact Messages ({stats.totalContacts})
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <Input
              placeholder="Search by name, phone, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background-paper border-white/10 h-12"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-background-paper border-white/10 h-12">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Data Table */}
        <div className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeTab === "franchise" ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-subtle">
                  <tr>
                    <th className="text-left p-4 text-foreground-muted font-medium">Name</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Phone</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">City</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Investment</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Status</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Date</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFranchiseLeads.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-foreground-muted">
                        No franchise leads found
                      </td>
                    </tr>
                  ) : (
                    filteredFranchiseLeads.map((lead, index) => (
                      <motion.tr
                        key={lead.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-t border-white/5 hover:bg-white/5"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="w-5 h-5 text-primary" />
                            </div>
                            <span className="text-foreground font-medium">{lead.name}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <a href={`tel:${lead.phone}`} className="text-foreground hover:text-primary">
                            {lead.phone}
                          </a>
                        </td>
                        <td className="p-4 text-foreground-muted">{lead.city}</td>
                        <td className="p-4">
                          <span className="text-primary font-medium">Rs {lead.investment} Lakh</span>
                        </td>
                        <td className="p-4">
                          <Select
                            value={lead.status}
                            onValueChange={(value) => handleStatusChange(lead.id, value, "franchise")}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs ${
                              lead.status === "pending" ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/30" :
                              lead.status === "contacted" ? "bg-blue-500/10 text-blue-500 border-blue-500/30" :
                              lead.status === "converted" ? "bg-green-500/10 text-green-500 border-green-500/30" :
                              "bg-red-500/10 text-red-500 border-red-500/30"
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="converted">Converted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4 text-foreground-muted text-sm">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowDetailModal(true);
                              }}
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-foreground-muted" />
                            </button>
                            <a
                              href={`https://wa.me/${lead.phone?.replace(/\D/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-green-500/10 rounded-lg transition-colors"
                              title="WhatsApp"
                            >
                              <Phone className="w-4 h-4 text-green-500" />
                            </a>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-subtle">
                  <tr>
                    <th className="text-left p-4 text-foreground-muted font-medium">Name</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Email</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Subject</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Status</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Date</th>
                    <th className="text-left p-4 text-foreground-muted font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-foreground-muted">
                        No contact messages found
                      </td>
                    </tr>
                  ) : (
                    filteredContacts.map((contact, index) => (
                      <motion.tr
                        key={contact.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-t border-white/5 hover:bg-white/5"
                      >
                        <td className="p-4 text-foreground font-medium">{contact.name}</td>
                        <td className="p-4">
                          <a href={`mailto:${contact.email}`} className="text-foreground-muted hover:text-primary">
                            {contact.email}
                          </a>
                        </td>
                        <td className="p-4 text-foreground-muted">{contact.subject}</td>
                        <td className="p-4">
                          <Select
                            value={contact.status}
                            onValueChange={(value) => handleStatusChange(contact.id, value, "contacts")}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs ${
                              contact.status === "pending" ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/30" :
                              "bg-green-500/10 text-green-500 border-green-500/30"
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="replied">Replied</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4 text-foreground-muted text-sm">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => {
                              setSelectedLead(contact);
                              setShowDetailModal(true);
                            }}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4 text-foreground-muted" />
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-background-paper border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {activeTab === "franchise" ? "Franchise Lead Details" : "Contact Details"}
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3 p-3 bg-background-subtle rounded-lg">
                <Users className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-foreground-muted text-xs">Name</p>
                  <p className="text-foreground font-medium">{selectedLead.name}</p>
                </div>
              </div>
              {selectedLead.phone && (
                <div className="flex items-center gap-3 p-3 bg-background-subtle rounded-lg">
                  <Phone className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-foreground-muted text-xs">Phone</p>
                    <a href={`tel:${selectedLead.phone}`} className="text-foreground font-medium hover:text-primary">
                      {selectedLead.phone}
                    </a>
                  </div>
                </div>
              )}
              {selectedLead.email && (
                <div className="flex items-center gap-3 p-3 bg-background-subtle rounded-lg">
                  <Mail className="w-5 h-5 text-secondary" />
                  <div>
                    <p className="text-foreground-muted text-xs">Email</p>
                    <a href={`mailto:${selectedLead.email}`} className="text-foreground font-medium hover:text-primary">
                      {selectedLead.email}
                    </a>
                  </div>
                </div>
              )}
              {selectedLead.city && (
                <div className="flex items-center gap-3 p-3 bg-background-subtle rounded-lg">
                  <MapPin className="w-5 h-5 text-accent" />
                  <div>
                    <p className="text-foreground-muted text-xs">City</p>
                    <p className="text-foreground font-medium">{selectedLead.city}</p>
                  </div>
                </div>
              )}
              {selectedLead.investment && (
                <div className="flex items-center gap-3 p-3 bg-background-subtle rounded-lg">
                  <IndianRupee className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-foreground-muted text-xs">Investment Budget</p>
                    <p className="text-primary font-medium">Rs {selectedLead.investment} Lakh</p>
                  </div>
                </div>
              )}
              {selectedLead.message && (
                <div className="p-3 bg-background-subtle rounded-lg">
                  <p className="text-foreground-muted text-xs mb-2">Message</p>
                  <p className="text-foreground text-sm">{selectedLead.message}</p>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 bg-background-subtle rounded-lg">
                <Calendar className="w-5 h-5 text-foreground-muted" />
                <div>
                  <p className="text-foreground-muted text-xs">Submitted On</p>
                  <p className="text-foreground font-medium">
                    {new Date(selectedLead.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
